let DEBUG_DATA_ENGINE = false;

/*
 * What we want is to fetch data from the server in some declarative way.
 *
 * We don't want to deal with:
 *
 * - aborting unnecessary requests
 * - managing async offline recovery, background updates, etc.
 * - asynchronous code based on promises
 *
 * We just want to declare what data we want in the store, and when the data
 * gets available, to update the store.
 *
 * All this can be achieved via a simple scheduler, that determines from
 * the current state what data are needed, generates a list of all
 * wanted remote resources.
 *
 * Fetcher engine then tries to resolve the wanted resource list and
 * passes the results to the next schedule call.
 *
 * This way we can have declarative remote resource syncing.
 */
DataFetchEngine = function() {
	let schedulers = [];
	let resourcesInFlight = {};
	let resolvedResources = [];
	let wantedResources = [];
	let resourceProviders = {};
	let generation = 1;

	let scheduleStatsResetTimer = setInterval(() => {
		for (let s of schedulers) {
			s.requests = 0;
		}
	}, 1000);

	function log(...args) {
		DEBUG_DATA_ENGINE && console.log(...args);
	}

	// we need to normalize resource params (sort object keys, drop null
	// values, to get stable resource keys, etc.)
	function normalizeObject(o) {
		let serializedKey = '';
		let keys = Object.keys(o).sort();

		for (let k of keys) {
			let v = o[k];
			if (v != null) {
				serializedKey += k + ':';

				if (typeof v == 'object' && v.constructor === Object) {
					serializedKey += '{' + normalizeObject(v) + '}';
				}

				serializedKey += ((typeof v == 'string') ? JSON.stringify(v) : v) + ',';
			}
		}

		return serializedKey;
	}

	function resourceKey(name, params) {
		if (params) {
			return name + '?' + normalizeObject(params);
		}

		return name;
	}

	function scheduleNow() {
		let wantedResources = [];
		let invocation = {
			generation,
			now: performance.now(),

			getResolvedResources() {
				return resolvedResources.slice();
			},

			getResource(name, params) {
				let rk = resourceKey(name, params);

				return resolvedResources.find(r => r.key == rk);
			},

			getResourceByKey(key) {
				if (!key) {
					return;
				}

				return resolvedResources.find(r => r.key == key);
			},

			wantResource(name, params) {
				let key = resourceKey(name, params);
				wantedResources.push({name, params, key});
				return key;
			},
		};

		for (let s of schedulers) {
			let wantedStart = wantedResources.length;

			if (s.disabled) {
				continue;
			}

			if (!(s.schedule instanceof Function)) {
				console.error('Disabling invalid scheduler: ', s.name);
				s.disabled = true;
				continue;
			}

			s.schedule(invocation);

			let schedulerWanted = wantedResources.slice(wantedStart);
			if (schedulerWanted.length > 0) {
				let undefinedResources = schedulerWanted.filter(r => !resourceProviders[r.name]);
				let newResources = schedulerWanted.filter(r => !resourcesInFlight[r.key])

				// if the resource can't be resolved, disable
				// the scheduler, and inform the programmer
				if (undefinedResources.length > 0) {
					console.error('Resource providers are not defined: ', undefinedResources.map(r => r.name));
					console.error('Disabling scheduler: ', s.name);
					s.disabled = true;
					wantedResources = wantedResources.slice(0, wantedStart);
					continue;
				}

				if (newResources.length > 0) {
					log('Scheduler ' + s.name + ' wants new resources: ', newResources);
					s.requests += newResources.length;
				}
			}

			// check for annoying bugs in a scheduler that lead to
			// infinite loop
			if (s.requests > 30) {
				console.error('Scheduler ' + s.name + ' requested 30 new resources in the last second, disabling');
				s.disabled = true;
			}
		}

		generation++;
		resolvedResources = [];
		syncResources(wantedResources);
	}

	function syncResources(wantedResources) {
		// de-dup
		let wantedMap = wantedResources.reduce((m, r) => {
			return m[r.key] = r, m;
		}, {});

		//console.log('resources wanted', Object.keys(wantedMap));
		//console.log('resources inflight', Object.keys(resourcesInFlight));

		// cancel in-flight
		for (let [key, r] of Object.entries(resourcesInFlight)) {
			if (!wantedMap[key]) {
				log('resource fetch abort', key, r);
				r.abortController.abort();
				delete resourcesInFlight[key];
			}
		}

		for (let [key, r] of Object.entries(wantedMap)) {
			// skip in-flight
			if (!resourcesInFlight[key]) {
				log('resource fetch', key, r);
				let abortController = new AbortController();

				resourceProviders[r.name](r.params, abortController.signal).then(
					res => {
						resolvedResources.push({
							name: r.name,
							params: r.params,
							key,
							ok: true,
							data: res,
						});

						delete resourcesInFlight[key];

						log('resource fetched', key, resolvedResources[resolvedResources.length - 1]);

						schedule();
					},
					err => {
						if (err instanceof AbortError) {
							return;
						}

						resolvedResources.push({
							name: r.name,
							params: r.params,
							key,
							ok: false,
							error: err,
						});

						delete resourcesInFlight[key];

						console.error('resource fetch failed', key, resolvedResources[resolvedResources.length - 1]);

						schedule();
					},
				);

				resourcesInFlight[key] = {
					abortController,
					r,
				};
			}
		}
	}

	function abort() {
		for (let [key, r] of Object.entries(resourcesInFlight)) {
			log('resource fetch abort', key, r);
			r.abortController.abort();
			delete resourcesInFlight[key];
		}
	}

	function registerResources(map) {
		Object.assign(resourceProviders, map);
	}

	function registerScheduler(s) {
		schedulers.push({
			position: 0,
			requests: 0,
			...s
		});

		schedulers.sort((a, b) => {
			return a.position - b.position;
		});
	}

	// Goals:
	// - throttle the scheduleNow calls to at most one per 33ms
	// - make scheduleNow happen at the start of the next frame

	let scheduleState = 'armed'; // armed, arming, firing, queued
	let scheduleTokenTimer;

	function schedule() {
		if (scheduleState == 'armed') {
			// we can call right away in the next frame
			setTimeout(() => {
				scheduleState = 'arming';
				scheduleTokenTimer = setTimeout(() => {
					let fire = scheduleState == 'queued';
					scheduleState = 'armed';

					if (fire) {
						schedule();
					}
				}, 10);

				scheduleNow();
			});

			scheduleState = 'firing';

		} else if (scheduleState == 'arming') {
			scheduleState = 'queued';
		}
	}

	// run schedule periodically (reduce the period when the page
	// is not visible)

	let timer;

	function updateTimer() {
		let hidden = document.visibilityState != 'visible';

		clearInterval(timer);
		timer = setInterval(schedule, hidden ? 60000 : 1000);
	}

	document.addEventListener('visibilitychange', ev => {
		schedule();
		updateTimer();
	});

	updateTimer();

	return {
		schedule,
		abort,
		resourceKey,
		registerScheduler,
		registerResources,
	};
}();

// }}}
// {{{ DataFetchEngine.Schedulers

/*
 * These are re-usable schedulers for fetching data from the server.
 *
 * Intended use is to call these functions from the toplevel schedulers
 * registered with registerScheduler.
 */
DataFetchEngine.Schedulers = {
	/**
	 * Get data from a provider:
	 *
	 * config:
	 * - cache_time - for how long a resource stays valid (ms)
	 * - cache_time_randomness - add additional random time to cache_time
	 *   in the range of (0..cache_time_randomness) * cache_time
	 *   default is 0.3
	 * - resource - name of the resource
	 * - params - params for the resource provider
	 * 
	 * data:
	 * - loaded - data are available (may or may not be outdated)
	 * - stale - stale data is available
	 * - error - last error
	 * - data_ts/error_ts/change_ts - time when the data/error were updated
	 * - fail_count - number of failed attempts to load the data
	 *   since the start or the last successful load
	 * - rk - resource key for the metadata/loaded data
	 * - loading - data is currently being loaded
	 * - loading_ts - ts of 'loading' attribute change
	 *
	 * This scheduler also implements staggered re-load if cache expired
	 * while the page was hidden.
	 */
	fetchResource(invocation, d, config) {
		let cache_time = config.cache_time || 0;
		let document_hidden = document.visibilityState != 'visible';
		let rk = DataFetchEngine.resourceKey(config.resource, config.params);
		if (!d || d.rk != rk) {
			let randomness_factor = config.cache_time_randomness != null ? config.cache_time_randomness : 0.3;

			d = {
				rk,
				fail_count: 0,
				loaded: false,
				cache_time: cache_time * (1 + Math.random() * randomness_factor),
				document_hidden,
			};
		}

		if (d.document_hidden !== document_hidden) {
			// document visibility change detected
			if (!document_hidden && d.cache_timeout < invocation.now) {
				// if the document was unhidden and the cache
				// timeout passed while it was hidden, extend
				// the timeout by 2s-4s fixed time
				//
				// this prevents thundering heard effect while 
				// switching through stale tabs quickly
				d.cache_timeout = invocation.now + 2000 * (1 + Math.random());
			}
		}

		d.document_hidden = document_hidden;

		let r = invocation.getResourceByKey(rk);
		if (r) {
			if (r.ok) {
				d.data_ts = invocation.now;
				d.cache_timeout = d.data_ts + d.cache_time;
				d.data = r.data;
				d.loaded = true;
				d.fail_count = 0;
				delete d.error;
				delete d.error_ts;
			} else {
				d.error_ts = invocation.now;
				d.error = r.error;
				d.fail_count++;
			}

			d.change_ts = invocation.now;
		}

		// issue resource request if resource:
		// - was not loaded yet and load did not fail
		// - is loaded and expired
		// - last load fail_count and exponential backoff expired
		let retry_max_backoff = 7; // 64 * retry_timeout
		let retry_timeout = 1000;
		let prevLoading = d.loading;

		d.loading = false;

		// don't do fetch anything when the tab is hidden
		if (!document_hidden) {
			if ((!d.loaded && !d.fail_count)
				|| (!d.fail_count && d.cache_timeout < invocation.now)
				|| (d.fail_count && d.error_ts + (retry_timeout * Math.pow(2, Math.min(d.fail_count, retry_max_backoff) - 1)) < invocation.now)) {

				invocation.wantResource(config.resource, config.params);
				d.loading = true;
			}
		}

		if (prevLoading !== d.loading) {
			d.loading_ts = invocation.now;
		}

		d.stale = !d.loaded || invocation.now > d.cache_timeout;

		return d;
	},

	/*
	 * Get resource just like with fetchResource and push the result
	 * to Store via a specified action.
	 *
	 * This handles a common pattern of keeping the store updated with
	 * data from the server, while properly handling errors and cache
	 * loads.
	 *
	 * Additional config properties:
	 *
	 * store  - store to dispatch to
	 * action - template for the action to invoke with `data` or `error`
	 *          properties
	 * cache_key (optional) - where to store state in the passed `cache` object
	 * store_has_data_key (optional) - a key under store that tells this function whether
	 *          the store has the data (this scheduler will load the
	 *          data immediately from cache if store indicates it doesn't
	 *          have the data)
	 */
	fetchResourceToStore(invocation, cache, config) {
		let store = config.store;
		let key = config.cache_key || config.resource;
		let res = cache[key] = DataFetchEngine.Schedulers.fetchResource(invocation, cache[key], config);

		function dispatch({data, error}) {
			store.dispatch({
				...config.action,
				data,
				error,
				loading: res.loading,
			});

			if (config.store_has_data_key) {
				store.dispatch({
					type: 'APPLY',
					[config.store_has_data_key]: true,
				});
			}
		}

		if (res.error_ts == invocation.now) {
			dispatch({
				error: res.error,
			});
		} else if (res.data_ts == invocation.now) {
			dispatch({
				data: res.data,
				error: null,
			});
		} else if (res.loading_ts == invocation.now && res.loading) {
			dispatch({});
		}

		if (config.store_has_data_key && !store.state[config.store_has_data_key]) {
			if (!res.stale) {
				dispatch({
					data: res.data,
					error: null,
				});
			} else if (res.error) {
				dispatch({
					error: res.error,
				});
			} else {
				dispatch({});
			}
		}

		return res;
	},
};
